import { useState, useRef } from "react";
import { AdminContainer } from "../components/layouts";
import MaterialTable from "material-table";
import {
  apiCollectionToRowResponse,
  rowQueryToApiParams
} from "../modules/tableQuery";
import api from "../api";
import { formatMoney } from "../lithic/Money";
import tableIcons from "../components/tableIcons";
import Link from "../components/Link";
import {
  FormControl,
  Select,
  MenuItem,
  Button,
  Box,
  Chip,
  CircularProgress
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import ConfirmationDialog from "../components/ConfirmationDialog";

const DropdownFilter = ({ columnDef, onFilterChanged, options }) => {
  const [selected, setSelected] = useState("");

  const handleChange = (event) => {
    const value = event.target.value;
    setSelected(value);
    onFilterChanged(columnDef.tableData.id, value === "" ? [] : [value]);
  };

  return (
    <FormControl variant="outlined" size="small" fullWidth>
      <Select value={selected} onChange={handleChange} displayEmpty>
        <MenuItem value="">All</MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const statusOptions = [
  { value: "draft", label: "Draft" },
  { value: "confirmed", label: "Confirmed" },
  { value: "processing", label: "Processing" },
  { value: "processed", label: "Processed" },
  { value: "failed", label: "Failed" },
  { value: "canceled", label: "Canceled" }
];

const payTypeOptions = [
  { value: "pay_in", label: "Invoice" },
  { value: "pay_out", label: "Payout" }
];

const statusColor = (status) => {
  switch (status) {
    case "draft":
      return "default";
    case "confirmed":
      return "primary";
    case "processing":
      return "primary";
    case "processed":
      return "primary";
    case "failed":
      return "secondary";
    case "canceled":
      return "default";
    default:
      return "default";
  }
};

export default function AggregatedPaymentsListPage() {
  const theme = useTheme();
  const history = useHistory();
  const tableRef = useRef();
  const [selectedRows, setSelectedRows] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const onBatchConfirmSuccess = (resp) => {
    const results = resp.data;
    const successCount = results.filter((r) => r.success).length;
    const failCount = results.length - successCount;
    const failMsg = failCount ? ", " + failCount + " failed" : "";
    toast.success("Confirmed " + successCount + " payment(s)" + failMsg);
    setSelectedRows([]);
    if (tableRef.current) tableRef.current.onQueryChange();
  };

  const executeBatchConfirm = (draftIds) => {
    setBatchLoading(true);
    api
      .batchConfirmAggregatedPayments({ ids: draftIds })
      .then(onBatchConfirmSuccess)
      .catch((err) => {
        toast.error(err.response?.data?.message || "Batch confirm failed");
      })
      .finally(() => {
        setBatchLoading(false);
        setConfirmDialog(null);
      });
  };

  const handleBatchConfirm = () => {
    const draftIds = selectedRows
      .filter((r) => r.status === "draft")
      .map((r) => r.id);

    if (!draftIds.length) {
      toast.warn("No draft payments selected for confirmation");
      return;
    }

    setConfirmDialog({
      title: "Process Payments",
      text: "This will process " + draftIds.length + " payment(s) for ACH transfer. This cannot be undone. Continue?",
      onConfirm: () => executeBatchConfirm(draftIds)
    });
  };

  const columns = [
    {
      title: "ID",
      field: "id",
      defaultSort: "desc",
      filtering: false,
      render: (rowData) => (
        <Link
          to={`/aggregated-payments/${rowData.id}`}
          style={{ color: theme.palette.dark.main }}
        >
          {rowData.id}
        </Link>
      )
    },
    {
      title: "Partner",
      field: "partnerId",
      sorting: false,
      filtering: false,
      render: (rowData) => (
        <Link
          to={`/partners/${rowData.partnerId}`}
          style={{ color: theme.palette.dark.main }}
        >
          Partner {rowData.partnerId}
        </Link>
      )
    },
    {
      title: "Status",
      field: "status",
      sorting: false,
      filtering: true,
      render: (rowData) => (
        <Chip
          size="small"
          label={rowData.status}
          color={statusColor(rowData.status)}
          variant={
            rowData.status === "canceled" || rowData.status === "draft"
              ? "outlined"
              : "default"
          }
        />
      )
    },
    {
      title: "Type",
      field: "payType",
      sorting: false,
      filtering: true,
      render: (rowData) =>
        rowData.payType === "pay_in" ? "Invoice" : "Payout"
    },
    {
      title: "Total",
      field: "totalAmount",
      sorting: false,
      filtering: false,
      render: (rowData) => (
        <span
          style={{
            fontWeight: "bold",
            color: rowData.payType === "pay_in" ? "green" : "red"
          }}
        >
          {formatMoney(rowData.totalAmount)}
        </span>
      )
    },
    {
      title: "Created",
      field: "createdAt",
      sorting: true,
      filtering: false,
      render: (rowData) =>
        rowData.createdAt
          ? dayjs(rowData.createdAt).format("MMM D, YYYY HH:mm")
          : "-"
    },
    {
      title: "Statements",
      field: "statementCount",
      sorting: false,
      filtering: false
    },
    {
      title: "Bank Account",
      field: "bankAccount",
      sorting: false,
      filtering: false,
      render: (rowData) =>
        rowData.bankAccount?.last4 ? `****${rowData.bankAccount.last4}` : "-"
    },
    {
      title: "ACH Transfer ID",
      field: "increaseAchTransferId",
      sorting: false,
      filtering: false,
      render: (rowData) => rowData.increaseAchTransferId || "-"
    },
    {
      title: "ACH Status",
      field: "increaseAchTransferStatus",
      sorting: false,
      filtering: false,
      render: (rowData) => rowData.increaseAchTransferStatus || "-"
    }
  ];

  const draftCount = selectedRows.filter((r) => r.status === "draft").length;

  return (
    <AdminContainer variant="list">
      <Box display="flex" justifyContent="flex-end" mb={2} style={{ gap: 8 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => history.push("/aggregated-payments/new")}
        >
          Create New
        </Button>
        {selectedRows.length > 0 && (
          <Button
            variant="contained"
            color="secondary"
            onClick={handleBatchConfirm}
            disabled={batchLoading}
          >
            {batchLoading ? (
              <CircularProgress size={20} />
            ) : (
              "Process " + draftCount + " Payments"
            )}
          </Button>
        )}
      </Box>

      <MaterialTable
        title="Aggregated Payments"
        tableRef={tableRef}
        columns={columns}
        options={{
          selection: true,
          selectionProps: (rowData) => ({
            disabled: rowData.status !== "draft",
            color: "primary"
          }),
          showSelectAllCheckbox: false,
          emptyRowsWhenPaging: false,
          pageSize: 50,
          pageSizeOptions: [50, 100],
          sorting: true,
          thirdSortClick: false,
          filtering: true
        }}
        onSelectionChange={(rows) => setSelectedRows(rows)}
        data={(q) => {
          const params = rowQueryToApiParams(q);
          return api
            .listAggregatedPayments(params)
            .then((resp) => apiCollectionToRowResponse(resp));
        }}
        icons={tableIcons}
        components={{
          FilterRow: (props) => (
            <tr>
              <td></td>
              {props.columns.map((columnDef) => (
                <td key={columnDef.tableData.id}>
                  {columnDef.field === "status" && (
                    <DropdownFilter
                      columnDef={columnDef}
                      onFilterChanged={props.onFilterChanged}
                      options={statusOptions}
                    />
                  )}
                  {columnDef.field === "payType" && (
                    <DropdownFilter
                      columnDef={columnDef}
                      onFilterChanged={props.onFilterChanged}
                      options={payTypeOptions}
                    />
                  )}
                </td>
              ))}
            </tr>
          )
        }}
      />

      {confirmDialog && (
        <ConfirmationDialog
          open={!!confirmDialog}
          title={confirmDialog.title}
          text={confirmDialog.text}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
          cancelProps={{ children: "No, go back" }}
          confirmProps={{
            children: batchLoading ? (
              <CircularProgress size={20} />
            ) : (
              "Yes, proceed"
            ),
            disabled: batchLoading
          }}
        />
      )}
    </AdminContainer>
  );
}
